// @ts-nocheck
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { Tooltip, TooltipContent, TooltipTrigger, TooltipProvider } from '@/components/ui/tooltip';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';

const schema = z.object({
  display_name: z.string().min(3).max(100),
  short_name: z.string().max(30).optional().or(z.literal('')),
  home_state: z.string().length(2).optional().or(z.literal('')),
  home_city: z.string().optional().or(z.literal('')),
  primary_color: z.string().optional().or(z.literal('')),
});

type FormData = z.infer<typeof schema>;

interface Props {
  team: any;
  onUpdated: () => void;
}

export default function TeamSettingsForm({ team, onUpdated }: Props) {
  const { user } = useAuth();
  const [saving, setSaving] = useState(false);
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [confirmInactive, setConfirmInactive] = useState(false);

  const { register, handleSubmit, formState: { errors } } = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      display_name: team.display_name ?? team.canonical_name ?? '',
      short_name: team.short_name ?? '',
      home_state: team.home_state ?? '',
      home_city: team.home_city ?? '',
      primary_color: team.primary_color ?? '#f97316',
    },
  });

  const uploadLogo = async (): Promise<string | null> => {
    if (!logoFile || !user) return null;
    const ext = logoFile.name.split('.').pop();
    const path = `${user.id}/${crypto.randomUUID()}.${ext}`;
    const { error } = await supabase.storage.from('team-logos').upload(path, logoFile);
    if (error) {
      toast.warning('Logo upload unavailable');
      return null;
    }
    const { data } = supabase.storage.from('team-logos').getPublicUrl(path);
    return data.publicUrl;
  };

  const onSubmit = async (values: FormData) => {
    setSaving(true);
    try {
      const logoUrl = await uploadLogo();
      const updates: any = { ...values };
      if (logoUrl) updates.logo_url = logoUrl;
      const { error } = await (supabase as any).from('teams').update(updates).eq('id', team.id);
      if (error) throw error;
      toast.success('Team updated');
      onUpdated();
    } catch (e: any) {
      toast.error(e.message);
    } finally {
      setSaving(false);
    }
  };

  const handleMarkInactive = async () => {
    const { error } = await (supabase as any).from('teams').update({ status: 'inactive' }).eq('id', team.id);
    if (error) {
      toast.error(error.message);
    } else {
      toast.success('Team marked inactive');
      onUpdated();
    }
    setConfirmInactive(false);
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Team settings</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            <div>
              <Label>Display name</Label>
              <Input {...register('display_name')} />
              {errors.display_name && <p className="text-sm text-destructive">{errors.display_name.message}</p>}
            </div>
            <div>
              <Label>Short name</Label>
              <Input {...register('short_name')} />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Home state</Label>
                <Input {...register('home_state')} maxLength={2} />
              </div>
              <div>
                <Label>Home city</Label>
                <Input {...register('home_city')} />
              </div>
            </div>
            <div>
              <Label>Primary color</Label>
              <Input type="color" {...register('primary_color')} className="h-10 w-20" />
            </div>
            <div>
              <Label>Logo</Label>
              <Input type="file" accept="image/*" onChange={(e) => setLogoFile(e.target.files?.[0] ?? null)} />
            </div>
            <Button type="submit" disabled={saving}>{saving ? 'Saving...' : 'Save changes'}</Button>
          </form>
        </CardContent>
      </Card>

      <Separator />

      <Card className="border-destructive/40">
        <CardHeader>
          <CardTitle className="text-destructive">Danger zone</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="inline-block">
                  <Button variant="outline" disabled>Transfer ownership</Button>
                </span>
              </TooltipTrigger>
              <TooltipContent>Coming soon</TooltipContent>
            </Tooltip>
          </TooltipProvider>
          <div>
            <Button variant="destructive" onClick={() => setConfirmInactive(true)}>Mark team inactive</Button>
          </div>
        </CardContent>
      </Card>

      <Dialog open={confirmInactive} onOpenChange={setConfirmInactive}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Mark team inactive?</DialogTitle>
          </DialogHeader>
          <p>This team will be hidden from active lists.</p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmInactive(false)}>Cancel</Button>
            <Button variant="destructive" onClick={handleMarkInactive}>Confirm</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
